import { IconCheck, IconX } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Flex, Grid, Input, Modal, Select, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import { useResolveApproval } from "@/common/artemis-operations/approvals";
import { useOrgUsers } from "@/common/artemis-operations/members";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import {
	type Approval,
	ApprovalRiskLevel,
	ApprovalStatus,
	approvalIsActionable,
} from "@/common/model";
import { formatNullableString } from "@/common/utils";
import { CustomIcon, DateField, IdField } from "../base";
import type { KanbanConfig } from "../kanban-board";
import type { ResponsiveTableProps } from "../responsive-table";
import { ResponsiveTableKanban } from "../responsive-table-kanban";
import { TableFilterBar } from "../table-filter-bar";
import {
	ApprovalRiskBadge,
	ApprovalRiskLabelMap,
	ApprovalStatusBadge,
	ApprovalStatusLabelMap,
	ApprovalTypeBadge,
} from "./approval-badges";
import { ApprovalDetailsDrawer } from "./approval-details-drawer";

type ApprovalRow = NonNullable<Approval>;

type ApprovalBoardColumn = "pending" | "approved" | "rejected";

// Exhaustive, so a new ApprovalStatus fails typecheck until it is placed on the board.
const BOARD_COLUMN_BY_STATUS: Record<ApprovalStatus, ApprovalBoardColumn> = {
	PENDING: "pending",
	APPROVED: "approved",
	// Legacy — approvals are no longer consumed, but old rows may still carry it.
	CONSUMED: "approved",
	REJECTED: "rejected",
	// Never approved; the card's badge shows which.
	EXPIRED: "rejected",
	INVALIDATED: "rejected",
};

// Board columns in lifecycle order; both decisions are final, so striped.
const KANBAN: KanbanConfig<ApprovalRow, ApprovalBoardColumn> = {
	columns: [
		{
			key: "pending",
			title: <Typography.Text strong>Pending</Typography.Text>,
		},
		{
			key: "approved",
			title: <Typography.Text strong>Approved</Typography.Text>,
			terminal: true,
		},
		{
			key: "rejected",
			title: <Typography.Text strong>Rejected</Typography.Text>,
			terminal: true,
			danger: true,
		},
	],
	getColumnKey: (approval) => BOARD_COLUMN_BY_STATUS[approval.status],
	getRowKey: (approval) => approval.id,
	renderCard: (approval) => (
		<Flex vertical gap={4}>
			<Flex justify="space-between" align="center" gap={8}>
				<Typography.Text strong ellipsis style={{ minWidth: 0 }}>
					{approval.name}
				</Typography.Text>
				<ApprovalRiskBadge status={approval.riskLevel} />
			</Flex>
			<Flex justify="space-between" align="center" gap={8}>
				<DateField value={approval.createdAt as string} />
				{/* Only where the column alone doesn't say it — Expired/Invalidated sit under Rejected. */}
				{/*{(approval.status === ApprovalStatus.EXPIRED ||
					approval.status === ApprovalStatus.INVALIDATED) && (
					<ApprovalStatusBadge status={approval.status} />
				)}*/}
			</Flex>
		</Flex>
	),
};

interface ApprovalTableProps
	extends Omit<ResponsiveTableProps<Approval>, "columns"> {
	workspaceId: string;
	// Controlled server-side search/filters (owned by the route via usePagedList).
	search?: string;
	onSearchChange?: (v: string) => void;
	status?: string;
	onStatusChange?: (v: string | undefined) => void;
	riskLevel?: string;
	onRiskLevelChange?: (v: string | undefined) => void;
}

export function ApprovalTable({
	workspaceId,
	data,
	loading,
	onRefresh,
	search,
	onSearchChange,
	status,
	onStatusChange,
	riskLevel,
	onRiskLevelChange,
	...props
}: ApprovalTableProps) {
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;
	const orgSlug = useRouteOrgSlug();
	const { confirm } = Modal;
	const resolveApproval = useResolveApproval(workspaceId);
	const { data: orgUsers } = useOrgUsers();
	const userById = useMemo(
		() => new Map((orgUsers ?? []).map((u) => [u.id, u])),
		[orgUsers],
	);
	const [currentApproval, setCurrentApproval] = useState<ApprovalRow | null>(
		null,
	);
	const rows = (data ?? []).filter((a): a is ApprovalRow => a != null);

	const handleResolve = (
		approvalId: string,
		decision: "APPROVED" | "REJECTED",
	) => {
		const isApprove = decision === "APPROVED";
		confirm({
			title: isApprove ? "Approve this request?" : "Reject this request?",
			content: isApprove
				? "The agent will be allowed to proceed with the described action."
				: "The agent action will be blocked and the task will be marked as failed.",
			okText: isApprove ? "Approve" : "Reject",
			okType: isApprove ? "primary" : "danger",
			onOk: () => resolveApproval.mutate({ approvalId, input: { decision } }),
		});
	};

	const columns: ColumnDef<ApprovalRow>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: "Name",
				cell: ({ row }) => (
					<Typography.Text strong ellipsis>
						{row.original.name}
					</Typography.Text>
				),
			},
			{
				accessorKey: "taskId",
				header: "Task",
				size: 170,
				cell: ({ row }) => (
					<IdField
						id={row.original.taskId}
						link={`/${orgSlug}/workspaces/${workspaceId}/tasks/${row.original.taskId}`}
					/>
				),
			},
			{
				accessorKey: "requestType",
				header: "Type",
				// Fits the longest label ("Credentials Request") on one line.
				size: 190,
				cell: ({ row }) => (
					<ApprovalTypeBadge type={row.original.requestType} />
				),
			},

			{
				accessorKey: "status",
				header: "Status",
				size: 140,
				filterFn: "equals",
				cell: ({ row }) => <ApprovalStatusBadge status={row.original.status} />,
			},
			{
				accessorKey: "riskLevel",
				header: "Risk",
				size: 110,
				filterFn: "equals",
				cell: ({ row }) => (
					<ApprovalRiskBadge status={row.original.riskLevel} />
				),
			},
			{
				accessorKey: "approvedBy",
				header: "Approved By",
				size: 200,
				cell: ({ row }) => {
					const id = row.original.resolvedBy;
					const user = id ? userById.get(id) : undefined;
					const label = user ? user.displayName || user.email || user.id : id;
					return <IdField id={formatNullableString(label)} />;
				},
			},
			{
				accessorKey: "createdAt",
				header: "Created",
				size: 120,
				enableSorting: true,
				cell: ({ row }) => (
					<DateField value={row.original.createdAt as string} />
				),
			},
			{
				// A pending request is only actionable until this passes, so approvers need to see it.
				accessorKey: "approvalWindowExpiresAt",
				header: "Expires",
				size: 120,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.approvalWindowExpiresAt ? (
						<DateField value={row.original.approvalWindowExpiresAt as string} />
					) : (
						"-"
					),
			},
			// {
			// 	accessorKey: "id",
			// 	header: "ID",
			// 	size: 200,
			// 	cell: ({ row }) => <IdField id={row.original.id} />,
			// },
		],
		[orgSlug, workspaceId, userById],
	);

	return (
		<>
			<ResponsiveTableKanban<ApprovalRow, ApprovalBoardColumn>
				kanban={KANBAN}
				loading={loading}
				initialSorting={[{ id: "createdAt", desc: true }]}
				columns={columns}
				data={rows}
				onRefresh={onRefresh}
				onRowClick={setCurrentApproval}
				rowActions={[
					{
						label: "Approve",
						icon: <CustomIcon icon={IconCheck} />,
						disabled: (approval) => !approvalIsActionable(approval),
						onClick: (approval) => handleResolve(approval.id, "APPROVED"),
					},
					{
						label: "Reject",
						icon: <CustomIcon icon={IconX} />,
						danger: true,
						disabled: (approval) => !approvalIsActionable(approval),
						onClick: (approval) => handleResolve(approval.id, "REJECTED"),
					},
				]}
				compactRender={(approval) => (
					<article
						style={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Space vertical size={2}>
							<Typography.Text strong id={approval.taskId}>
								{approval.name}
							</Typography.Text>
							<ApprovalTypeBadge type={approval.requestType} />
						</Space>
						<Space orientation="vertical" align="end" size={2}>
							<ApprovalStatusBadge status={approval.status} />
							<ApprovalRiskBadge status={approval.riskLevel} />
						</Space>
					</article>
				)}
				filterBar={() => (
					<TableFilterBar isCompact={isCompact}>
						<Input.Search
							placeholder="Search by name, ID or task ID"
							allowClear
							style={{ width: isCompact ? "100%" : 220 }}
							value={search ?? ""}
							onChange={(e) => onSearchChange?.(e.target.value)}
							onSearch={(v) => onSearchChange?.(v)}
						/>
						<Select
							allowClear
							placeholder="Status"
							style={{ width: isCompact ? "100%" : 160 }}
							value={status ?? null}
							onChange={(v) => onStatusChange?.(v ?? undefined)}
							options={Object.values(ApprovalStatus).map((s) => ({
								value: s,
								label: ApprovalStatusLabelMap[s],
							}))}
						/>
						<Select
							allowClear
							placeholder="Risk"
							style={{ width: isCompact ? "100%" : 140 }}
							value={riskLevel ?? null}
							onChange={(v) => onRiskLevelChange?.(v ?? undefined)}
							options={Object.values(ApprovalRiskLevel).map((r) => ({
								value: r,
								label: ApprovalRiskLabelMap[r],
							}))}
						/>
					</TableFilterBar>
				)}
				{...props}
			/>
			<ApprovalDetailsDrawer
				open={!!currentApproval}
				approval={currentApproval ?? undefined}
				workspaceId={workspaceId}
				onClose={() => setCurrentApproval(null)}
			/>
		</>
	);
}
